import {
  type ScreenEventPayload,
  type TrackEventPayload,
  type UIEventPayload
} from '@atlassiansox/analytics-web-client';
import { type FieldType } from '@databases/model/field';
import { type CollabLayoutType } from '@databases/model/views/view';
import { type PanelKeys } from '@databases/panel-types';
import {
  type ActionType,
  type DatabaseFieldType,
  type ExportFileType,
  type PageSubType,
  type ParentPageOption,
  type SelectionMode
} from '@databases/types/database';
import {
  type DatabaseCalculationType,
  type DatabaseFilterType,
  type DatabaseGroupSortType,
  type DatabaseLayout,
  type DatabaseLayoutType
} from '@databases/types/database/view';

import { type DatasourceType } from '@canvas/atlassian-service/datasource';

export interface GenericEventData {
  contentId: string;
  databaseContainer: DatabaseContainerType;
}

export interface FirstEditEventData {
  contentId: string;
  isAIGenerated: boolean;
}

export interface SelectionsAnalyticsAttributes {
  databaseEntryCount?: number;
  databaseFieldCount?: number;
}

export enum MacroType {
  DATABASE = 'DATABASE',
  ENTRY = 'ENTRY',
  VALUE = 'VALUE'
}

export interface SendMacroCreatedEventData {
  macroType: MacroType;
  isNew: boolean;
  contentId?: string;
  source?: string;
}

export enum DatabaseContainerType {
  STANDALONE_DATABASE = 'STANDALONE_DATABASE',
  EMBED_DATABASE = 'EMBED_DATABASE',
  STANDALONE_ENTRY = 'STANDALONE_ENTRY',
  EMBED_ENTRY = 'EMBED_ENTRY',
  EMBED_VALUE = 'EMBED_VALUE',
  NATIVE_EMBED = 'NATIVE_EMBED',
  NONE = 'NONE'
}

export type CustomTrackEventPayload = TrackEventPayload & {
  attributes: {
    databaseContainer: DatabaseContainerType;
  };
};

export type FirstEditTrackEventPayload = TrackEventPayload & {
  objectType: 'database';
  attributes: {
    isAIGenerated: boolean;
  };
};

export type CustomCommentsEventPayload = CustomTrackEventPayload & {
  attributes: {
    commentType?: string;
    parentCommentContentId?: string;
  };
};

export type CustomUIEventPayload = UIEventPayload & {
  attributes: {
    databaseContainer: DatabaseContainerType;
  };
};

export type CustomScreenEventPayload = ScreenEventPayload & {
  attributes?: {
    databaseContainer?: DatabaseContainerType;
  };
};

export type AddEntryEventSource =
  | 'tableLayout'
  | 'boardLayout'
  | 'galleryLayout'
  | 'timelineLayout';

export interface AddEntryEventData extends GenericEventData {
  source: AddEntryEventSource;
}

export type TimelineEntryDraggedEventData = GenericEventData;

export type TimelineEntryResizedEventData = GenericEventData;

export interface GroupReorderedEventData extends GenericEventData {
  numberOfGroups: number;
  isSubgroup: boolean;
  source: GroupEventSource;
}

export interface DatasourcesEventData extends GenericEventData {
  databaseContainer: DatabaseContainerType;
  datasourceType: DatasourceType;
  totalEntries: number;
}

export interface ImportEntriesEventData extends GenericEventData {
  isEmptyDatabase: boolean;
  fileSize: number;
  importMethod: 'append' | 'replace' | 'map';
}

export type UndoType =
  | 'successfulImport'
  | 'entriesFiltered'
  | 'mismatchedFieldType'
  | 'restrictedContentCantBeCopied'
  | 'structureLocked'
  | 'updateFieldSettings';

export interface UndoEventData extends GenericEventData {
  source: 'flags' | 'snackbar';
  action: 'paste' | 'import';
  type: UndoType;
}

export interface ImportCancelledEventData extends GenericEventData {
  source: 'importDialogStepOne' | 'importDialogStepTwo';
}

export interface CellContentHasBeenModifiedEventData
  extends GenericEventData, SelectionsAnalyticsAttributes {
  activationId?: string;
  fieldType: DatabaseFieldType;
}

export interface ExportDatabaseEventData {
  applyViewOnExport: boolean;
  databaseId: string;
  databaseContainer: DatabaseContainerType;
  exportFormat: ExportFileType;
}

export interface HideColumnsEventData {
  contentId?: string;
  source: 'databaseView' | 'databaseViewSettingsPanel' | 'databaseViewSettingsMenu';
  databaseContainer: DatabaseContainerType;
  hidden: boolean;
  fieldType: DatabaseFieldType | null;
}

export interface HideAllColumnsEventData {
  contentId?: string;
  source: 'databaseViewSettingsPanel' | 'databaseViewSettingsMenu';
  databaseContainer: DatabaseContainerType;
  hidden: boolean;
}

export interface FrozenColumnsUpdatedEventData
  extends GenericEventData, SelectionsAnalyticsAttributes {
  action: 'pinned' | 'unpinned';
  source: 'fieldContextualMenu';
  previousFrozenColumnCount: number;
  nextFrozenColumnCount: number;
  targetFieldIndex: number;
  maxFrozenColumnCount: number;
}

export interface LayoutSelectedEventData extends GenericEventData {
  layoutType: DatabaseLayoutType;
  source:
    | 'databaseViewSettingsPicker'
    | 'databaseViewSettingsPanel'
    | 'databaseViewSettingsMenu';
}

export interface LayoutSettingSelectedEventData extends GenericEventData {
  layoutSetting:
    | 'cardWidth'
    | 'coverImage'
    | 'groupField'
    | 'dateField'
    | 'timelineScale';
  source:
    | 'databaseViewSettingsPicker'
    | 'databaseViewSettingsPanel'
    | 'databaseViewSettingsMenu'
    | 'timelineLayout';
  value?: string;
}

export interface NumberedEntriesToggledEventData extends GenericEventData {
  source: 'TableLayoutOptions' | 'databaseViewSettingsMenu';
  value: boolean;
}

export interface ReplaceDatabaseClickedEventData extends GenericEventData {
  source: 'datasourceDialog' | 'templates' | 'import';
}

export enum SendDatabaseSortEventSources {
  SORT_MENU = 'databaseSortMenu',
  FIELD_MENU = 'databaseFieldMenu',
  FIELD_NAME = 'databaseFieldName',
  VIEW_PANEL_MENU = 'databaseViewSettingsPanel',
  VIEW_SETTINGS_MENU = 'databaseViewSettingsMenu'
}

export interface SendDatabaseSortEventData {
  contentId?: string;
  fieldId?: string; // undefined if resetting sort
  isAscending?: boolean; // undefined if resetting sort
  source: SendDatabaseSortEventSources;
  fieldType?: DatabaseFieldType;
  databaseContainer: DatabaseContainerType;
}

export interface SendDatabaseFilterData extends GenericEventData {
  fieldType?: DatabaseFieldType;
  filterType: DatabaseFilterType;
  source:
    | 'databaseFilterMenu'
    | 'databaseFieldActions'
    | 'databaseViewSettingsPanel'
    | 'databaseViewSettingsMenu';
}

export interface SendDatabaseCreatedViewEventData extends ViewEventData {
  layoutType: DatabaseLayoutType;
}

export interface SendDatabaseEditViewEventData extends ViewEventData {
  source: 'databaseViewActionsMenu';
}

export interface SendDatabaseDuplicateViewEventData extends ViewEventData {
  layoutType: DatabaseLayoutType;
}

export enum SendDatabaseDeleteEntryEventSources {
  DATABASE_ENTRY_DIALOG = 'databaseEntryDialog',
  DELETE_ENTRY_CONFIRM_DIALOG = 'deleteEntryConfirmationDialog',
  ENTRY_CONTEXTUAL_MENU = 'entryContextualMenu',
  ACTION_BAR = 'actionBar'
}

export interface SendDeleteEntryEvent extends GenericEventData {
  source: SendDatabaseDeleteEntryEventSources;
}

export interface SendConnectionClickedEventData extends GenericEventData {
  connectionType: 'connectJira' | 'connectConfluence';
}

export interface SendCreateDatabaseEntryEventData extends GenericEventData {
  source: 'databaseView' | 'slashCommand';
}

export interface SendCreatePageFromDatabaseEventData extends GenericEventData {
  newPageId: string;
  parentId: string;
  pageLocation: ParentPageOption;
  templateId: string | null;
  hasEntryEmbed: boolean;
  pageSubType: PageSubType;
}

export interface SendPageCreatedByFlowEventData extends GenericEventData {
  isLivePage: boolean;
}

export interface SendCreateDatabaseFieldEventData extends GenericEventData {
  source: 'databaseCreateFieldDialog' | 'boardLayout' | 'calendarLayout';
  fieldType: DatabaseFieldType;
}

export interface SendDeleteFieldEventData extends GenericEventData {
  fieldTypes: DatabaseFieldType[];
}

export interface TemplateClickedEventData extends GenericEventData {
  isInitialDatabase: boolean;
  templateType:
    | 'productRequirements'
    | 'budget'
    | 'campaignTracker'
    | 'newHire'
    | 'contentLibrary'
    | 'contactLibrary'
    | 'researchRepository'
    | 'emptyDefault'
    | 'feedbackTracker'
    | 'meetingNotes'
    | 'capacityPlanning'
    | 'contentCalendar'
    | 'applicantTracker'
    | 'okrTracker'
    | 'vendorManagement'
    | 'legacyContentLibrary';
}

export interface ViewEventData extends GenericEventData {
  source:
    | 'databaseUnsavedViewChangesBanner'
    | 'databaseViewActionsMenu'
    | 'databaseViewPicker'
    | 'databaseViewSettingsPanel'
    | 'databaseToolbar'
    | 'databaseViewTabs'
    | 'databaseViewSettingsMenu';
  viewId: string;
  isEdited?: boolean;
}

export interface SystemTemplateEventData extends GenericEventData {
  templateType: 'simpleDatabase';
  templateLayout?: CollabLayoutType;
}

export interface GroupByFieldSelectedEventData extends GenericEventData {
  selectedFieldType: DatabaseFieldType;
  isSubgroup: boolean;
  source: GroupEventSource;
}

export interface ViewUpdatedEventData extends ViewEventData {
  sortUpdated: boolean;
  filterUpdated: boolean;
  groupUpdated: boolean;
  hideFieldsUpdated: boolean;
  layoutTypeUpdated: boolean;
  layoutSettingsUpdated: boolean;
  renamed: boolean;
  selectedLayout: DatabaseLayoutType;
  contextualMenuExperimentCohort: 'control' | 'test';
}

export interface SetUpPanelTabClickedEventData extends GenericEventData {
  isInitialDatabase: boolean;
  actionSubjectId: string;
}

export interface DatabaseCreateCalculationEventData extends GenericEventData {
  source: 'quickCalcFieldActions' | 'quickCalculationSelect';
  fieldId: string;
  fieldType: DatabaseFieldType;
  calculationType: DatabaseCalculationType;
}

export interface DatabaseRemoveCalculationEventData extends GenericEventData {
  source:
    | 'quickCalcFieldActions'
    | 'quickCalculationSelect'
    | 'selectFieldMenu'
    | 'selectValueMenu'
    | 'deleteFieldButton';
  fieldId: string;
  fieldType: DatabaseFieldType;
}

export interface DatabaseCalculationTypeConvertedData extends GenericEventData {
  source: 'quickCalcFieldActions' | 'quickCalculationSelect';
  fieldId: string;
  fieldType: DatabaseFieldType;
  fromCalculationType: DatabaseCalculationType;
  toCalculationType: DatabaseCalculationType;
}

export interface EntryLinkFieldLoadData extends GenericEventData {
  source: 'fieldNameWithActions';
  fieldId: string;
}

export interface SendDatabaseContentViewedData extends GenericEventData {
  locale: string;
}

export interface FieldOptionEventData extends GenericEventData {
  source:
    | 'fieldSettingsDialog'
    | 'database'
    | 'createFieldDialog'
    | 'datasourceDialog'
    | 'boardLayout'
    | 'calendarLayout';
  fieldSelection: DatabaseFieldType;
  isNewField: boolean;
}

export interface PageCreationFieldSettingEditedEventData extends GenericEventData {
  fieldId: string;
  fieldSetting:
    | 'pageLocation'
    | 'parentItem'
    | 'pageTemplate'
    | 'displayEntryOnPage'
    | 'pageSubType';
  value: string | null;
}

export interface JiraIssueEventData extends GenericEventData {
  source: 'jiraIssueDetailsSettings';
  jiraFieldSelection: string;
}

export interface JiraFieldUpdatedEventData extends GenericEventData {
  issueKey: string;
  fieldType: 'status' | 'assignee' | 'priority' | 'summary';
}

export interface DatasourcesDBMigrationEventData extends GenericEventData {
  source: 'FieldNameWithActions';
  missingField: 'cloudId' | 'serverId';
  cloudId?: string;
  serverId?: string;
}

export interface DatabaseProfiledEventData extends GenericEventData {
  contentId: string;
  attributes: {
    entriesCount: number;
    fieldsCount: number;
    fieldTypeCounts: Record<FieldType, number>;
    fieldTypes: FieldType[];
    viewsCount: number;
    viewSettings: {
      layoutType: DatabaseLayout['type'];
      showNumberedEntriesEnabled: boolean;
      cardWidth: string;
    };
  };
}

export interface SendMentionNotificationCheckboxToggledEvent extends GenericEventData {
  checked: boolean;
}

export interface MentionNotificationFlagViewedEventData extends GenericEventData {
  type: 'success' | 'restricted' | 'error';
}

type CheckboxState = 'selected' | 'unselected';
export interface SendRowCheckboxSelectedEventData
  extends SelectionsAnalyticsAttributes, GenericEventData {
  entryId: string;
  checkboxState: CheckboxState;
}

export interface SendHeaderCheckboxSelectedEventData
  extends SelectionsAnalyticsAttributes, GenericEventData {
  entriesCount: number;
  checkboxState: CheckboxState;
}

export interface SendSelectedActionItemEventData
  extends GenericEventData, SelectionsAnalyticsAttributes {
  //trigger ActionTypes like "move" | "add" | "delete" will not be sent
  type: ActionType;
  selectedCount: number;
  selectionMode: SelectionMode;
}

export type GroupEventSource = 'databaseGroupPanel' | 'databaseViewSettingsMenu';

export interface RemoveSubgroupEventData extends GenericEventData {
  fieldType: DatabaseFieldType;
  source: GroupEventSource;
}

export interface GroupSortTypeChangedEventData extends GenericEventData {
  selectedType: DatabaseGroupSortType;
  numberOfGroups: number;
  isSubgroup: boolean;
  source: GroupEventSource;
}

export interface AddGroupButtonEventData extends GenericEventData {
  numberOfGroups: number;
  isSubgroup: boolean;
  source: GroupEventSource;
}

export interface AddGroupSelectedEventData extends GenericEventData {
  isSubgroup: boolean;
  selectedFieldType: DatabaseFieldType;
  source: GroupEventSource;
}

export interface SubgroupCollapseExpandEventData extends GenericEventData {
  state: 'expanded' | 'collapsed';
}

export interface AddGroupFieldPopupSelectedEventData extends GenericEventData {
  selectedFieldType: DatabaseFieldType;
}

export interface GroupButtonClickedEventData extends GenericEventData {
  source: 'databaseToolbar' | 'boardLayoutPanel' | 'databaseViewSettingsPanel';
  group: DatabaseFieldType | undefined;
  subgroup: DatabaseFieldType | undefined;
}

export interface SortButtonClickedEventData extends GenericEventData {
  source: 'databaseToolbar';
  sortCount: number;
}

export interface FilterButtonClickedEventData extends GenericEventData {
  source: 'databaseToolbar';
  filterCount: number;
}

export interface HideFieldsButtonClickedEventData extends GenericEventData {
  source: 'databaseToolbar';
  hiddenFieldCount: number;
}

export interface DragToFillAppliedEventData
  extends GenericEventData, SelectionsAnalyticsAttributes {}

export interface ColumnReorderedEventData
  extends GenericEventData, SelectionsAnalyticsAttributes {}

export interface EntryReorderedEventData
  extends GenericEventData, SelectionsAnalyticsAttributes {
  entryType: 'entryRow' | 'card';
}

export interface PanelResizedEventData extends GenericEventData {
  panelKey: PanelKeys;
  panelSize: number;
}
export interface SendRichTextFormattingAppliedEventData extends GenericEventData {
  formattingType: string;
}

export interface MediaFormatFieldSettingClickedEventData extends GenericEventData {
  mediaFormat: string;
}

export interface SendAIResultActionedEventData {
  contentId: string;
  messageAction: string;
  databaseContainer: DatabaseContainerType;
  totalElements: number;
  remainingElements: number;
  singleInstrumentationID: string;
}

export interface ValueContextMenuOpenedEventData
  extends GenericEventData, SelectionsAnalyticsAttributes {}

export interface SortRemovedEventData extends GenericEventData {
  source: SendDatabaseSortEventSources;
}

export interface SortClearedEventData extends GenericEventData {
  source: SendDatabaseSortEventSources;
}

export type FilterEventSource = SendDatabaseFilterData['source'];

export interface FilterRemovedEventData extends GenericEventData {
  isFilterGroup: boolean;
  source: FilterEventSource;
}

export interface FilterClearedEventData extends GenericEventData {
  source: FilterEventSource;
}
